import { useContext, useEffect, useState } from 'react';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import { AccountContext } from '@/context/account.context';
import { LocationContext } from '@/context/location.context';
import { localStorageService, sessionStorageService, clearAssessmentAnswers, clearReferralStorage } from '@/utils/local_storage';
import useLocalStorageSession from '@/hooks/useLocalStorageSession';
import { useRouter } from 'next/router';
import { bookmarkHelpers } from '../utils/helpers';
import useMixpanel from '@/hooks/useMixpanel';
import Auth, { GOOGLE_PROVIDER } from '@/utils/api/odas/auth';
import { languageContext } from '@/context/language.context';
import isEmail from 'validator/lib/isEmail';
import t from '@/utils/Language/Modal/LoginModal-translations';
import useHoneybadger from '@/hooks/useHoneybadger';
import { emailOrPhone, isSafeRoute, loadScript } from '@/utils/utils';
import Keys from '@/constants/Keys';
import publicRoutes from '@/public-routes.json';
import { isReactPage } from '@/helpers/Link';

const useAccount = () => {
  const { currentLanguage } = useContext(languageContext);
  const modal = useContext(modalContext);
  const { addToast } = useContext(toastContext);
  const account = useContext(AccountContext);
  const locationContext = useContext(LocationContext);
  const { setSessionValue, deleteSessionValue } = useLocalStorageSession();
  const router = useRouter();
  const { track } = useMixpanel();
  const loginProvider = 'one-degree'; // used for Mixpanel tracking
  const { setHoneybadgerContext, notifyHoneybadger } = useHoneybadger();

  // Auth.getProvider() reads document.cookie (browser-only), so reading it synchronously during render causes
  // a server/client hydration mismatch. Avoid this by setting the value after render in useEffect.
  const [provider, setProvider] = useState(null);
  const isGoogleUser = provider === GOOGLE_PROVIDER;
  const isActiveDirectoryUser = !!(provider && provider.startsWith('saml_'));

  useEffect(() => {
    let providerCookie = Auth.getProvider();
    setProvider(providerCookie || null);
    if (providerCookie) {
      setSessionValue('provider', providerCookie);
      // initialize a new saml session on the first page-load
      if (router?.query?.samlinit && Auth.getToken()) {
        const { samlinit, ...queryWithoutParam } = router.query;
        router.replace({ query: queryWithoutParam }, undefined, { shallow: true });
        Auth.getAccountInfo()
          .then((data) => {
            processLogin(data, router.pathname.indexOf('/onboarding-saml') === 0);
          })
          .catch((err) => {
            addToast({
              message: t('failed_login'),
              delayed: 10000,
              type: 'error'
            });
          });
      }
    }
  }, []);

  useEffect(() => {
    if (Auth.isLoggedIn() && Auth.sessionIsExpired) {
      account.setIsLoggedIn(false);
      if (router.pathname != '/confirm-login') {
        confirmSessionModal();
      }
    }
  }, [Auth.sessionIsExpired]);

  const getAffiliation = async () => {
    try {
      await account.loadAffiliation();
    } catch (error) {
      console.error('error', error);
      addToast({
        message: t('failed_login'),
        delayed: 10000,
        type: 'error'
      });
      // setMessage(error?.message);
    }
  };

  const parseForm = (e) => {
    const formData = new FormData(e.target);
    return Object.fromEntries(formData.entries());
  };

  const acctLogin = async ({ login_key, password }, mfa = {}) => {
    const params = { login_key, password };
    try {
      const data = await Auth.authLogin(params, mfa || null);
      return processLogin(data);
    } catch (err) {
      addToast({
        message: t('failed_login'),
        delayed: 10000,
        type: 'error'
      });
    }
  };

  const confirmSessionModal = () => {
    modal.enqueueModal({ name: 'ReloginModal' }, { forceOpen: true });
  };

  const confirm = (e, { handleFail = null } = {}) => {
    handleFail ||= () => {}; // no-op
    if (e) {
      e.preventDefault();
      const formData = new FormData(e.target),
        formDataObj = Object.fromEntries(formData.entries());
      return Auth.confirm({ password: formDataObj.password })
        .then((data) => {
          if (data) {
            account.storeUserInfo(data.user);
            Auth.sessionIsExpired = false;
            modal?.closeModal();
            account.setIsLoggedIn(true);
            if (router?.query?.r) {
              window.location.href = isSafeRoute(router?.query?.r) ? router.query.r : '/';
            } else if (router.pathname == '/login' || router.pathname == '/signup' || router.pathname == '/confirm-login') {
              // Let's reroute if on any of these pages and authenticate successfully
              window.location.href = '/';
            }
          } else {
            addToast({
              message: t('We could not confirm you own this account. Please re-enter your password and try again.'),
              delayed: 10000,
              type: 'warning'
            });
            handleFail();
          }
        })
        .catch((error) => {
          addToast({
            message: t('We could not confirm you own this account. Please re-enter your password and try again.'),
            delayed: 10000,
            type: 'warning'
          });
          handleFail();
        });
    } else {
      handleFail({ error: 'missing parameter e' });
    }
  };

  const signup = (e, args) => {
    e.preventDefault();
    const formData = new FormData(e.target),
      formDataObj = Object.fromEntries(formData.entries());
    const loginKey = isEmail(formDataObj.emailOrPhone) ? 'email' : 'cell_phone';
    const user = { password: formDataObj.password, is_professional: !!formDataObj.is_professional };
    if (args?.signupInvitation) {
      if (!user.is_professional) {
        user.is_professional = !!args.signupInvitation.is_professional;
      }
      user.organization_id = args.signupInvitation.organization_id;
      user.invitation_id = args.signupInvitation.invitation_id;
      user.invitation_token = args.signupInvitation.invitation_token;
      delete args.signupInvitation;
    }
    user[loginKey] = formDataObj.emailOrPhone;
    return Auth.signup(user, args).then((data) => {
      if (data) {
        return processSignup(data);
      } else {
        return false;
      }
    });
  };

  const clearUser = () => {
    const p = Auth.logoutUser();
    localStorage.setItem('user', JSON.stringify({})); // TODO consider removing this in late 2023. This is needed for now due to us storing directly in `user` in local storage. But going forward we should be using our localStorage service which prepends `.ls` which means eventually we should only be storing in `ls.user`
    return p;
  };

  // `redirectTo` sends the member somewhere specific instead of the homepage, and navigates even from a
  // public route — used when the app ends the session deliberately rather than at the member's request.
  const logout = ({ redirectTo } = {}) => {
    const path = router.asPath.split('?')[0].split('/')[1];

    deleteSessionValue('provider');
    bookmarkHelpers.clearBookmarkCache();
    account.setAccessEvents([]);
    account.setBookmarks({});
    account.storeUserInfo({});
    account.setIsLoggedIn(false);
    setHoneybadgerContext({});

    clearReferralStorage();

    if (redirectTo || path === 'assessments' || !publicRoutes.routes.includes(path)) {
      window.location.href = redirectTo || '/';
    }

    sessionStorageService.clear();
    clearAssessmentAnswers();
    return clearUser();
  };

  const handleLoginWithGooglePlus = (authResponse) => {
    return Auth.handleLoginWithGooglePlus(authResponse).then((data) => {
      if (data.isCreation) {
        return processSignup(data);
      } else {
        return !!processLogin(data);
      }
    });
  };

  const displayGoogleButton = (el, customCallback) => {
    if (typeof customCallback !== 'function') {
      customCallback = (result) => result;
    }
    return loadScript('//accounts.google.com/gsi/client')
      .then((res) => {
        return google.accounts.id.initialize({
          client_id: Keys.googlePlus.clientId,
          callback: (authResponse) => {
            handleLoginWithGooglePlus(authResponse).then(customCallback);
          }
        });
      })
      .then(() => {
        google.accounts.id.renderButton(document.getElementById(el), {
          theme: 'outline',
          type: 'standard',
          shape: 'pill',
          text: 'continue_with',
          size: 'large',
          logo_alignment: 'center',
          width: '300px',
          locale: currentLanguage
        });
      })
      .catch((ex) => {
        notifyHoneybadger(ex);
      });
  };

  // `data` arrives in one of two shapes depending on the caller:
  //   - already unwrapped, `{ user, ... }` — a completed Auth.authLogin, Auth.getAccountInfo, or Google login
  //   - a raw axios response, so the body sits under `.data` — Auth.authLogin when there's no jwt yet (MFA challenge)
  // That's why the MFA branch below reads/returns `data.data` while the logged-in branch reads `data.user`.
  const processLogin = (data, skipRedirect) => {
    if (data) {
      if ([400, 401, 404].includes(data?.response?.status)) {
        const message = t(data.response.data?.message) || t('failed_login');
        addToast({
          message: message,
          delayed: 10000,
          type: 'error'
        });
        return false;
      } else if (data?.data?.token_hash) {
        // requires MFA — return the unwrapped body so callers get `{ token_hash, mfa_method }` (see LoginForm).
        // No redirect here: the member isn't logged in until they submit the one-time password.
        return data.data;
      } else {
        // logged in
        account.setIsLoggedIn(true);
        account.storeUserInfo(data.user);
        getAffiliation();
        setHoneybadgerContext({
          user_id: data?.user?.id
        });
        track('Login', { provider: loginProvider });
        if (data.user?.latitude && data.user?.longitude) {
          locationContext.updateLocation({
            locationName: data.user.location,
            lat: data.user.latitude,
            lng: data.user.longitude,
            state: data.user.state,
            county: data.user.county
          });
        }
        if (data.user?.region) {
          locationContext.updateLocation({
            region: data.user.region
          });
        }
      }
    }
    if (!skipRedirect) {
      openNextPage();
    }
    // Whatever reaches here is normally the already-unwrapped shape — a 400/401/404 response or the MFA flow would
    // have returned above. (It can also be falsy, which skips the block entirely, or the raw response from authLogin's
    // couldn't-load-the-account fallback.) Either way, return `data` as-is rather than reaching into `.data`: callers
    // use it as the "did the login succeed" signal (i.e. LoginForm -> LoginModal's onAuthenticated, and the
    // `!!processLogin(data)` in handleLoginWithGooglePlus).
    return data;
  };

  const processSignup = (data) => {
    if ([400, 401, 404].includes(data?.response?.status)) {
      const message = t(
        "We couldn't create your account. Make sure you are using a real email address or cell phone number. If you think you already have an account, you can try logging in."
      );
      addToast({
        message: message,
        delayed: 10000,
        type: 'error'
      });
      return false;
    }
    // modal box for affiliation

    track('user_created', { provider: loginProvider });
    account.setIsLoggedIn(true);
    account.storeUserInfo(data?.user);
    setHoneybadgerContext({
      user_id: data?.user?.id
    });
    return data;
  };

  const openNextPage = () => {
    const nextPage = account.nextPage;
    const nextPageTarget = account.nextPageTarget;
    account.setNextPage(false);
    account.setNextPageTarget(false);
    if (nextPage) {
      let decodedNextPage = decodeURIComponent(nextPage);
      if (nextPageTarget) {
        window.open(decodedNextPage, nextPageTarget);
      } else {
        if (isReactPage(decodedNextPage)) {
          router.push(decodedNextPage);
        } else if (window) {
          window.location.href = decodedNextPage;
        }
      }
    } else if (router?.query?.r) {
      window.location.href = isSafeRoute(router?.query?.r) ? router.query.r : '/';
    } else if (router.pathname == '/login' || router.pathname == '/signup' || router.pathname == '/confirm-login') {
      // on login and signup, we set nextPage but there is something about Google auth flow that unsets the
      // state variable in the account context. So here we handle that case specifically.
      // Let's always get off confirm-login if there isn't a valid reroute and the user authenticated
      window.location.href = '/';
    }
    modal?.closeModal();
  };

  const setLocation = (e) => {
    return () => {
      let userObj = localStorageService.get('user') || {};
      userObj = Object.assign(userObj, { locale: e ? e.language : 'en' }, locationContext.getLocation() || {});
      if (userObj.location) {
        return account.updateAccountInfo(userObj);
      }
      return null;
    };
  };

  const forgotPassword = (e) => {
    let formDataObj = e;
    if (e.target) {
      e.preventDefault();
      const formData = new FormData(e.target);
      formDataObj = Object.fromEntries(formData.entries());
    }
    const emailOrPhoneKey = emailOrPhone(formDataObj.emailOrPhone);
    return emailOrPhoneKey
      ? Auth.reset({ [emailOrPhoneKey]: formDataObj.emailOrPhone }).then((data) => {
          return 'success';
        })
      : null;
  };

  return {
    parseForm,
    account,
    provider,
    isGoogleUser,
    isActiveDirectoryUser,
    acctLogin,
    signup,
    logout,
    confirm,
    openNextPage,
    confirmSessionModal,
    displayGoogleButton,
    setLocation,
    forgotPassword
  };
};

export default useAccount;
